import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, 
  Circle, 
  FileText, 
  ShieldCheck, 
  GraduationCap, 
  Activity,
  Upload,
  ChevronRight,
  AlertCircle
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const onboardingSteps = [
  {
    id: "profile",
    title: "Complete Profile",
    description: "Fill in your personal and professional details",
    icon: FileText,
    items: ["Personal Information", "Contact Details", "Work Experience", "Specializations"]
  },
  {
    id: "documents",
    title: "Upload Documents",
    description: "Submit required verification documents",
    icon: Upload,
    items: ["NRIC / MyKad", "Professional License", "Degree Certificate", "CPR Certificate"]
  },
  {
    id: "verification",
    title: "Verification",
    description: "Document review by our team",
    icon: ShieldCheck,
    items: ["License Verification", "Background Check", "Credential Review"]
  },
  {
    id: "training",
    title: "Complete Training",
    description: "Finish required LMS modules",
    icon: GraduationCap,
    items: ["Platform Orientation", "Clinical Guidelines", "Safety Protocols", "Assessment Skills"]
  },
  {
    id: "activation",
    title: "Get Activated",
    description: "Start receiving session requests",
    icon: Activity,
    items: ["Trial Session", "Final Review", "Account Activation"]
  }
];

export default function TherapistOnboarding() {
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, profileRes] = await Promise.all([
          axios.get(`${API}/therapist/stats`, { withCredentials: true }),
          axios.get(`${API}/therapist/profile`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setProfile(profileRes.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Calculate completion based on status
  const statusProgress = {
    pending: 1,
    verified: 3,
    active: 5
  };
  
  const currentStep = statusProgress[stats?.status] || 1;
  const overallProgress = (currentStep / 5) * 100;
  const lmsProgress = stats?.lms_completion || 0;

  return (
    <div className="space-y-6" data-testid="therapist-onboarding">
      {/* Progress Overview */}
      <Card className="bg-gradient-to-br from-primary-50 to-white border-primary-100">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="font-heading font-bold text-2xl text-slate-900">Onboarding Progress</h2>
              <p className="text-slate-600 mt-1">Complete all steps to start receiving patients</p>
            </div>
            <div className="text-center">
              <div className="relative w-24 h-24 mx-auto">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#e2e8f0"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#F40d07"
                    strokeWidth="8"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${overallProgress * 2.51} 251`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-heading font-bold text-2xl text-slate-900">{Math.round(overallProgress)}%</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Onboarding Steps */}
      <div className="space-y-4">
        {onboardingSteps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep - 1;
          const Icon = step.icon;
          
          return (
            <Card 
              key={step.id}
              className={`transition-all ${
                isCurrent ? 'border-primary shadow-glow' : 
                isCompleted ? 'bg-slate-50' : 'opacity-60'
              }`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted ? 'bg-emerald-100' : isCurrent ? 'bg-primary-50' : 'bg-slate-100'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                    ) : (
                      <Icon className={`w-6 h-6 ${isCurrent ? 'text-primary' : 'text-slate-400'}`} />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-slate-900">
                          {step.title}
                        </h3>
                        <p className="text-sm text-slate-500">{step.description}</p>
                      </div>
                      <Badge className={
                        isCompleted ? 'bg-emerald-100 text-emerald-700' :
                        isCurrent ? 'bg-primary-50 text-primary' :
                        'bg-slate-100 text-slate-500'
                      }>
                        {isCompleted ? 'Completed' : isCurrent ? 'In Progress' : 'Pending'}
                      </Badge>
                    </div>
                    
                    {(isCurrent || isCompleted) && (
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                        {step.items.map((item, i) => {
                          const itemCompleted = isCompleted || (isCurrent && i < 2); // Mock: first 2 items completed in current step
                          return (
                            <div 
                              key={i}
                              className={`flex items-center gap-2 text-sm ${
                                itemCompleted ? 'text-emerald-600' : 'text-slate-500'
                              }`}
                            >
                              {itemCompleted ? (
                                <CheckCircle className="w-4 h-4" />
                              ) : (
                                <Circle className="w-4 h-4" />
                              )}
                              {item}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {isCurrent && (
                      <Button className="mt-4 bg-primary hover:bg-primary-600" data-testid={`continue-${step.id}-btn`}>
                        Continue <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* LMS Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-primary" />
            Training Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Overall LMS Completion</span>
              <span className="font-bold text-slate-900">{lmsProgress}%</span>
            </div>
            <Progress value={lmsProgress} className="h-3" />
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              {[
                { name: "Platform Orientation", progress: 100 },
                { name: "Clinical Guidelines", progress: lmsProgress > 50 ? 100 : 60 },
                { name: "Safety Protocols", progress: lmsProgress > 75 ? 100 : 40 },
                { name: "Assessment Skills", progress: lmsProgress > 90 ? 100 : 20 },
              ].map((module) => (
                <div key={module.name} className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-900 mb-2">{module.name}</p>
                  <Progress value={module.progress} className="h-2" />
                  <p className="text-xs text-slate-500 mt-1">{module.progress}%</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-blue-900">Need Help?</h3>
              <p className="text-sm text-blue-700 mt-1">
                Our onboarding team is here to assist you. Contact us at support@physiomobile.my 
                or call +60 3-1234 5678 during business hours.
              </p>
              <Button variant="outline" className="mt-3 border-blue-200 text-blue-700 hover:bg-blue-100" data-testid="contact-support-btn">
                Contact Support
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
